import React from "react";
import Head from "next/head";
import NavBar from "@/components/templates/NavBar";
import Slider from "@/components/templates/Slider";
import PromotionsCarousel from "@/components/templates/PromotionsCarousel";
import Reviews from "@/components/templates/Reviews";
import Newsletter from "@/components/templates/Newsletter";
import Footer from "@/components/templates/Footer";
import Booking from "@/components/templates/Booking";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import WelcomeRooms from "@/components/templates/WelcomeRooms";
import TabRooms from "@/components/templates/TabRooms";
import AmenitiesGrid from "@/components/templates/AmenitiesGrid";

import allactive from "@/public/images/icons/room-category-all-active.svg";
import all from "@/public/images/icons/room-category-all.svg";
import oceanfrontactive from "@/public/images/icons/room-category-oceafront-active.svg";
import oceanfront from "@/public/images/icons/room-category-oceafront.svg";
import lagoonactive from "@/public/images/icons/room-category-lagoon-active.svg";
import lagoon from "@/public/images/icons/room-category-lagoon.svg";
import QueryString from "qs";
import {
  apiUrl,
  apiRooms,
  apiRoomCategories,
  apiPromotions,
  axiosGet,
} from "@/utils/api";
import moment from 'moment';
const currentDate = moment(new Date()).format('YYYY-MM-DD,  HH:MM:SS');

export default function Page(props) {
  const {
    rooms = [],
    categories = [],
    promotions = [],
    noIndex,
    locale = "es",
  } = props;
  const { t } = useTranslation("common", "rooms");
  const sliders = t("rooms:sliders", { returnObjects: true });
  const filters = categories.length
    ? [
        {
          tab: locale === "es" ? "TODO" : "ALL",
          icon: all,
          iconActive: allactive,
          tabs: "all",
        },
        ...categories.map((category) => {
          return {
            tab: category.name,
            icon: {
              src: apiUrl + category.icon_inactive.url,
              width: category.icon_inactive.width,
              height: category.icon_inactive.height,
              alt: category.icon_inactive_alt || "",
            },
            iconActive: {
              src: apiUrl + category.icon_active.url,
              width: category.icon_active.width,
              height: category.icon_active.height,
              alt: category.icon_active_alt || "",
            },
            tabs: category.id,
          };
        }),
      ]
    : [
        {
          tab: t("rooms:rooms_categories.all"),
          icon: all,
          iconActive: allactive,
          tabs: "all",
        },
        {
          tab: t("rooms:rooms_categories.ocean"),
          icon: oceanfront,
          iconActive: oceanfrontactive,
          tabs: "oceanfront",
        },
        {
          tab: t("rooms:rooms_categories.lagoon"),
          icon: lagoon,
          iconActive: lagoonactive,
          tabs: "lagoon",
        },
      ];

  const amenities = t("rooms:amenities", { returnObjects: true });
  const reviews = t("rooms:reviews", { returnObjects: true });
  return (
    <>
      <Head>
        <title>{t("rooms:meta.title")}</title>
        <meta name="description" content={t("rooms:meta.description")} />
        <meta name="og:title" content={t("rooms:meta.title")} />
        <meta name="og:description" content={t("rooms:meta.description")} />
        <meta name="keywords" content={t("rooms:meta.keywords")} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {noIndex && <meta name="robots" content="noindex" />}
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={t('common:canonical_pages.rooms')} />
      </Head>
      <main>
        <NavBar />
        <Booking />
        <Slider sliders={sliders} />
        <WelcomeRooms t={t} />
        <TabRooms
          t={t}
          filters={filters}
          rooms={rooms || t("rooms:rooms", { returnObjects: true })}
          from={t("common:booking.from") || ""}
          priceNight={t("rooms:rooms_details", { returnObjects: true }) || {}}
        />
        <AmenitiesGrid t={t} amenities={amenities} />
        <div className="block md:hidden">
          <PromotionsCarousel
            promotions={
              promotions.length
                ? promotions
                : t("common:promotions", { returnObjects: true })
            }
            isNavigation={true}
            styles={{ themeColor: "#F18A00" }}
          />
        </div>
        <div className="hidden md:block">
          <PromotionsCarousel
            promotions={
              promotions.length
                ? promotions
                : t("common:promotions", { returnObjects: true })
            }
            isNavigation={false}
            styles={{ themeColor: "#F18A00" }}
          />
        </div>
        <Reviews
          title={{
            text: t("rooms:reviews_title"),
          }}
          text={{
            text: t("rooms:reviews_text"),
          }}
          reviews={reviews}
        />
        <Newsletter gtmFormId="room-newsletter" />
        <Footer />
      </main>
    </>
  );
}

export async function getServerSideProps({ locale }) {
  const noIndex = process.env.ENV_NO_INDEX || false;
  const roomsQuery = QueryString.stringify(
    {
      locale,
      sort: "order:asc",
      populate: {
        room_category: {
          fields: ["id", "name"],
        },
        room_sliders: {
          fields: ["id", "image_alt"],
          populate: {
            image: {
              fields: ["url", "width", "height"],
            },
          },
          sort: "order:asc",
        },
        localizations: {
          fields: ["slug"],
          populate: {
            room_sliders: {
              fields: ["id", "image_alt"],
              populate: {
                image: {
                  fields: ["url", "width", "height"],
                },
              },
              sort: "order:asc",
            },
          },
        },
      },
    },
    {
      encodeValuesOnly: true,
    }
  );
  const rooms = await axiosGet(apiRooms, roomsQuery);
  const roomCategoriesQuery = QueryString.stringify(
    {
      locale,
      fields: ["id", "name", "icon_active_alt", "icon_inactive_alt"],
      populate: {
        icon_active: {
          fields: ["url", "width", "height"],
        },
        icon_inactive: {
          fields: ["url", "width", "height"],
        },
      },
    },
    {
      encodeValuesOnly: true,
    }
  );
  const categories = await axiosGet(apiRoomCategories, roomCategoriesQuery);
  const promotionsQuery = QueryString.stringify(
    {
      locale,
      sort: "order:desc",
      fields: [
        "name",
        "slug",
        "description",
        "thumbnail_alt, link",
        "price_from",
        'expiration_date',
        'start_date',
      ],
      populate: {
        thumbnail: {
          fields: ["url", "width", "height"],
        },
        localizations: {
          fields: ["slug"],
        },
      },
    },
    {
      encodeValuesOnly: true,
    }
  );
  const promotions = await axiosGet(apiPromotions, promotionsQuery);
  const validPromos = promotions.filter(
    (p) => {
      let initial_validity = false
      let final_validity = false
      initial_validity = !p.start_date && true || moment(p.start_date).format('YYYY-MM-DD,  HH:MM:SS')<=currentDate && true || false
      final_validity = !p.expiration_date && true || moment(p.expiration_date).format('YYYY-MM-DD,  HH:MM:SS')>currentDate && true || false
      return( initial_validity===true && final_validity===true )}
  );
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "rooms", "routes"])),
      rooms: rooms.length ? rooms : false,
      categories,
      promotions: validPromos,
      locale,
      noIndex,
    },
  };
}
